import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';
import './GateCreateModal.css';

/**
 * GateCreateModal - Modal for creating logic gates (AND, OR, NOT, XOR)
 * 
 * Gates are special nodes with input and output synapses.
 * This modal allows selecting gate type and connecting to input/output nodes.
 */

const GATE_TYPES = [
  { value: 'AND', label: 'AND Gate', description: 'All inputs must be active' },
  { value: 'OR', label: 'OR Gate', description: 'At least one input must be active' },
  { value: 'NOT', label: 'NOT Gate', description: 'Inverts the input signal' },
  { value: 'XOR', label: 'XOR Gate', description: 'Exactly one input must be active' },
];

function GateCreateModal({
  isOpen,
  onClose,
  onGateCreated,
  graphId,
}) {
  // Form state
  const [gateType, setGateType] = useState('AND');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [condition, setCondition] = useState('');
  const [selectedInputs, setSelectedInputs] = useState([]);
  const [selectedOutputs, setSelectedOutputs] = useState([]);
  
  // Node search state
  const [nodes, setNodes] = useState([]);
  const [nodesLoading, setNodesLoading] = useState(false);
  const [nodeSearchQuery, setNodeSearchQuery] = useState('');
  
  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load nodes when modal opens
  const loadNodes = useCallback(async () => {
    if (!graphId) return;
    
    setNodesLoading(true);
    try {
      const response = await apiService.listNodes({
        graphId,
        pageSize: 500,
      });
      if (response.success) {
        setNodes(response.nodes || []);
      }
    } catch (err) {
      console.error('Failed to load nodes:', err);
    } finally {
      setNodesLoading(false);
    }
  }, [graphId]);

  useEffect(() => {
    if (isOpen) {
      loadNodes();
      // Reset form
      setGateType('AND');
      setName('');
      setDescription('');
      setCondition('');
      setSelectedInputs([]);
      setSelectedOutputs([]);
      setError(null);
      setSuccess(null);
    }
  }, [isOpen, loadNodes]);

  // Auto-generate name based on gate type
  useEffect(() => {
    if (!name || name === 'AND Gate' || name === 'OR Gate' || name === 'NOT Gate' || name === 'XOR Gate') {
      setName(`${gateType} Gate`);
    }
  }, [gateType, name]);

  // Filter nodes based on search (includes aliases)
  const filteredNodes = nodes.filter(node => {
    if (!nodeSearchQuery) return true;
    const q = nodeSearchQuery.toLowerCase();
    const nodeName = (node.name || node.nodeName || '').toLowerCase();
    const nodeType = (node.type || node.nodeType || '').toLowerCase();
    const aliases = (node.aliases || []).map(a => (a || '').toLowerCase());
    return nodeName.includes(q) || nodeType.includes(q) || aliases.some(a => a.includes(q));
  });

  // Handle input node toggle
  const toggleInputNode = (nodeId) => {
    setSelectedInputs(prev => {
      if (prev.includes(nodeId)) {
        return prev.filter(id => id !== nodeId);
      }
      return [...prev, nodeId];
    });
  };

  // Handle output node toggle
  const toggleOutputNode = (nodeId) => {
    setSelectedOutputs(prev => {
      if (prev.includes(nodeId)) {
        return prev.filter(id => id !== nodeId);
      }
      return [...prev, nodeId];
    });
  };

  // Handle form submission
  const handleCreate = async () => {
    // Validation
    if (!gateType) {
      setError('Please select a gate type');
      return;
    }
    if (selectedInputs.length === 0) {
      setError('Please select at least one input node');
      return;
    }
    if (selectedOutputs.length === 0) {
      setError('Please select at least one output node');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        graphId,
        gateType,
        name: name.trim() || `${gateType} Gate`,
        description: description.trim() || `${gateType} logic gate`,
        inputs: selectedInputs,
        outputs: selectedOutputs,
      };

      if (condition.trim()) {
        payload.condition = condition.trim();
      }

      const response = await apiService.createGate(payload);

      if (response.success) {
        setSuccess('Gate created successfully');
        
        // Notify parent and close after short delay
        setTimeout(() => {
          if (onGateCreated) {
            onGateCreated();
          }
          onClose();
        }, 1000);
      } else {
        throw new Error(response.message || 'Failed to create gate');
      }
    } catch (err) {
      console.error('Failed to create gate:', err);
      setError(err.response?.data?.message || err.message || 'Failed to create gate');
    } finally {
      setSaving(false);
    }
  };

  // Get node name by ID
  const getNodeName = (nodeId) => {
    const node = nodes.find(n => (n.id || n.nodeId) === nodeId);
    return node ? (node.name || node.nodeName || 'Unknown') : 'Unknown';
  };

  if (!isOpen) return null;

  return (
    <div className="gate-modal-overlay" onClick={onClose}>
      <div className="gate-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="gate-modal-header">
          <div>
            <p className="gate-modal-eyebrow">Logic Gate</p>
            <h2>Create Gate</h2>
          </div>
          <button
            type="button"
            className="gate-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="gate-modal-body">
          {/* Messages */}
          {error && (
            <div className="gate-message error">
              {error}
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}
          {success && (
            <div className="gate-message success">
              {success}
            </div>
          )}

          {/* Gate Type Selection */}
          <div className="gate-form-section">
            <h3>Gate Type</h3>
            <div className="gate-type-grid">
              {GATE_TYPES.map(type => (
                <button
                  key={type.value}
                  type="button"
                  className={`gate-type-option ${gateType === type.value ? 'selected' : ''}`}
                  onClick={() => setGateType(type.value)}
                >
                  <span className="gate-type-label">{type.label}</span>
                  <span className="gate-type-desc">{type.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Basic Info */}
          <div className="gate-form-section">
            <h3>Basic Information</h3>
            <div className="gate-form-group">
              <label htmlFor="gate-name">Name</label>
              <input
                id="gate-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Gate name"
              />
            </div>
            <div className="gate-form-group">
              <label htmlFor="gate-description">Description</label>
              <textarea
                id="gate-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the purpose of this gate"
                rows={2}
              />
            </div>
            <div className="gate-form-group">
              <label htmlFor="gate-condition">Condition Expression (optional)</label>
              <input
                id="gate-condition"
                type="text"
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
                placeholder="e.g., threshold > 0.5"
              />
            </div>
          </div>

          {/* Node Selection */}
          <div className="gate-form-section">
            <h3>Connect Nodes</h3>
            
            {/* Search */}
            <div className="gate-node-search">
              <input
                type="text"
                placeholder="Search nodes..."
                value={nodeSearchQuery}
                onChange={(e) => setNodeSearchQuery(e.target.value)}
              />
            </div>

            {nodesLoading ? (
              <div className="gate-loading">Loading nodes...</div>
            ) : (
              <div className="gate-node-columns">
                {/* Input Nodes */}
                <div className="gate-node-column">
                  <h4>
                    Input Nodes
                    <span className="gate-count">({selectedInputs.length} selected)</span>
                  </h4>
                  <div className="gate-node-list">
                    {filteredNodes.length === 0 ? (
                      <p className="gate-no-nodes">No nodes found</p>
                    ) : (
                      filteredNodes.map(node => {
                        const nodeId = node.id || node.nodeId;
                        const isSelected = selectedInputs.includes(nodeId);
                        return (
                          <label
                            key={`input-${nodeId}`}
                            className={`gate-node-item ${isSelected ? 'selected' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleInputNode(nodeId)}
                            />
                            <span className="node-info">
                              <span className="node-name">{node.name || node.nodeName}</span>
                              <span className="node-type">{node.type || node.nodeType || 'concept'}</span>
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Output Nodes */}
                <div className="gate-node-column">
                  <h4>
                    Output Nodes
                    <span className="gate-count">({selectedOutputs.length} selected)</span>
                  </h4>
                  <div className="gate-node-list">
                    {filteredNodes.length === 0 ? (
                      <p className="gate-no-nodes">No nodes found</p>
                    ) : (
                      filteredNodes.map(node => {
                        const nodeId = node.id || node.nodeId;
                        const isSelected = selectedOutputs.includes(nodeId);
                        return (
                          <label
                            key={`output-${nodeId}`}
                            className={`gate-node-item ${isSelected ? 'selected' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleOutputNode(nodeId)}
                            />
                            <span className="node-info">
                              <span className="node-name">{node.name || node.nodeName}</span>
                              <span className="node-type">{node.type || node.nodeType || 'concept'}</span>
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Selected Summary */}
          {(selectedInputs.length > 0 || selectedOutputs.length > 0) && (
            <div className="gate-form-section gate-summary">
              <h3>Connection Summary</h3>
              <div className="gate-connection-preview">
                <div className="gate-preview-inputs">
                  {selectedInputs.map(id => (
                    <span key={id} className="gate-preview-node">
                      {getNodeName(id)}
                      <button onClick={() => toggleInputNode(id)}>×</button>
                    </span>
                  ))}
                  {selectedInputs.length === 0 && <span className="gate-preview-empty">No inputs</span>}
                </div>
                <div className="gate-preview-gate">
                  <span className="gate-preview-arrow">→</span>
                  <span className="gate-preview-type">{gateType}</span>
                  <span className="gate-preview-arrow">→</span>
                </div>
                <div className="gate-preview-outputs">
                  {selectedOutputs.map(id => (
                    <span key={id} className="gate-preview-node">
                      {getNodeName(id)}
                      <button onClick={() => toggleOutputNode(id)}>×</button>
                    </span>
                  ))}
                  {selectedOutputs.length === 0 && <span className="gate-preview-empty">No outputs</span>}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="gate-modal-footer">
          <button
            type="button"
            className="btn-cancel"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-create"
            onClick={handleCreate}
            disabled={saving || selectedInputs.length === 0 || selectedOutputs.length === 0}
          >
            {saving ? 'Creating...' : 'Create Gate'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GateCreateModal;
